import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Toaster } from '../../components/ui/sonner';
import { GraduationCap, Clock, Globe as GlobeIcon, ArrowLeft, Loader2, Sparkles, MessageCircle, CheckCircle2 } from 'lucide-react';
import { CourseVisual } from '../../components/practical-courses/CourseVisual';
import { courseInquiryAPI, settingsAPI } from '../../lib/api';

const backend = process.env.REACT_APP_BACKEND_URL;

export default function PracticalCourseDetailPage() {
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [showContact, setShowContact] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

    useEffect(() => {
        (async () => {
            try {
                const [courseRes, settingsRes] = await Promise.all([
                    fetch(`${backend}/api/practical-courses/${slug}`),
                    settingsAPI.getPublic().catch(() => ({ data: {} })),
                ]);
                if (courseRes.status === 404) {
                    setNotFound(true);
                    return;
                }
                const data = await courseRes.json();
                setCourse(data.course);
                setWhatsappNumber(settingsRes.data?.whatsapp_number || '');
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    const openWhatsApp = (inquiry) => {
        if (!whatsappNumber) return;
        const text = `Hi, I'm interested in the course: *${course.title}* (${course.provider_name}).%0A%0A` +
            `Name: ${inquiry.name}%0AEmail: ${inquiry.email}%0APhone: ${inquiry.phone}` +
            (inquiry.message ? `%0AMessage: ${inquiry.message}` : '');
        const num = whatsappNumber.replace(/^\+/, '');
        window.open(`https://wa.me/${num}?text=${text}`, '_blank', 'noopener,noreferrer');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            toast.error('Name, email and phone are required');
            return;
        }
        setSubmitting(true);
        try {
            await courseInquiryAPI.submit({
                course_id: course.course_id,
                course_title: course.title,
                ...form,
            });
            toast.success('Great! Opening WhatsApp so we can continue there.');
            openWhatsApp(form);
            setShowContact(false);
            setForm({ name: '', email: '', phone: '', message: '' });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to send. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div></Layout>;
    }
    if (notFound || !course) {
        return (
            <Layout>
                <div className="max-w-3xl mx-auto py-20 px-4 text-center">
                    <GraduationCap className="w-16 h-16 mx-auto text-neutral-300 mb-3" />
                    <h1 className="text-2xl font-bold text-neutral-900">Course not found</h1>
                    <p className="text-neutral-600 mt-2">The course you're looking for may have been removed.</p>
                    <Link to="/practical-courses"><Button className="mt-6">Back to Courses</Button></Link>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Toaster />
            {/* Hero */}
            <div className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-950 to-violet-950 text-white">
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                    <Link to="/practical-courses" className="inline-flex items-center gap-1 text-sm text-primary-200 hover:text-white mb-6">
                        <ArrowLeft className="w-4 h-4" /> All Courses
                    </Link>
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <div className="w-24 h-24 rounded-2xl bg-white/10 backdrop-blur border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                            {course.provider_logo_url ? (
                                <img src={course.provider_logo_url.startsWith('/') ? `${backend}${course.provider_logo_url}` : course.provider_logo_url} alt={course.provider_name} className="max-w-[70%] max-h-[70%] object-contain" />
                            ) : (
                                <CourseVisual category={course.category} size="hero" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm text-primary-200 mb-2">
                                {course.is_featured && <span className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4" /> Featured</span>}
                                <span>{course.provider_name}</span>
                            </div>
                            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" data-testid="course-detail-title">{course.title}</h1>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {course.category && <Badge className="bg-primary-500/20 text-primary-100 border-primary-400/30">{course.category}</Badge>}
                                {course.country && <Badge className="bg-white/10 border-white/10 text-white">{course.country === 'Canada' ? '🇨🇦 Canada' : '🇬🇧 UK'}</Badge>}
                                {course.duration && <Badge className="bg-white/10 border-white/10 text-white"><Clock className="w-3 h-3 mr-1" />{course.duration}</Badge>}
                                {course.format && <Badge className="bg-white/10 border-white/10 text-white"><GlobeIcon className="w-3 h-3 mr-1" />{course.format}</Badge>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <div className="prose prose-lg max-w-none prose-headings:text-neutral-900 prose-p:text-neutral-700 prose-a:text-primary-600" dangerouslySetInnerHTML={{ __html: course.long_description || `<p>${course.short_description}</p>` }} />

                    {course.tags?.length > 0 && (
                        <div className="mt-8">
                            <div className="text-sm font-semibold text-neutral-700 mb-2">Topics covered</div>
                            <div className="flex flex-wrap gap-2">
                                {course.tags.map((t) => <Badge key={t} variant="secondary" className="bg-primary-50 text-primary-700">{t}</Badge>)}
                            </div>
                        </div>
                    )}
                </div>

                <aside className="lg:col-span-1">
                    <div className="sticky top-24 bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                        <div className="text-sm text-neutral-500 mb-1">Price</div>
                        <div className="text-2xl font-bold text-neutral-900 mb-4">{course.price || 'Contact us'}</div>

                        <ul className="text-sm text-neutral-700 space-y-2 mb-6">
                            <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary-600" /> {course.duration || 'Flexible'}</li>
                            <li className="flex items-center gap-2"><GlobeIcon className="w-4 h-4 text-primary-600" /> {course.format || 'Online'}</li>
                            <li className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary-600" /> {course.provider_name}</li>
                        </ul>

                        <Button
                            onClick={() => setShowContact(true)}
                            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white font-semibold"
                            data-testid="course-whatsapp-cta"
                        >
                            <MessageCircle className="w-5 h-5 mr-2" /> Contact us on WhatsApp
                        </Button>

                        <ul className="text-xs text-neutral-600 mt-4 space-y-1.5">
                            <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> Share your details, we'll guide you personally.</li>
                            <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> Payment discussed after a quick chat.</li>
                            <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" /> We connect you to the right provider.</li>
                        </ul>
                    </div>
                </aside>
            </div>

            {/* Contact Modal */}
            <Dialog open={showContact} onOpenChange={setShowContact}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-emerald-600" /> Enquire about this course
                        </DialogTitle>
                        <DialogDescription>
                            Fill in your details and we'll continue the conversation on WhatsApp.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div>
                            <Label htmlFor="ci-name">Full name *</Label>
                            <Input id="ci-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required data-testid="inquiry-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="ci-email">Email *</Label>
                                <Input id="ci-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required data-testid="inquiry-email" />
                            </div>
                            <div>
                                <Label htmlFor="ci-phone">Phone *</Label>
                                <Input id="ci-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44..." required data-testid="inquiry-phone" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="ci-msg">Message (optional)</Label>
                            <Textarea id="ci-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything specific you'd like to know?" rows={3} data-testid="inquiry-message" />
                        </div>
                        {!whatsappNumber && (
                            <p className="text-xs text-amber-600">Note: our WhatsApp number isn't configured yet — your enquiry will still be saved and we'll contact you.</p>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowContact(false)}>Cancel</Button>
                            <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inquiry-submit">
                                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><MessageCircle className="w-4 h-4 mr-2" /> Send & Open WhatsApp</>}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
